// ###### IMPORTS
import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { CloudArrowUpIcon, CloudArrowDownIcon } from '@heroicons/react/24/solid';
import {
  importAssetsExcel,
  exportAssetsExcel,
  deleteAllAssetsData,
} from '../../viewServices/assets_api';
import { SHEET_NAMES, buildWorkbookFromPayload } from '../../viewServices/assets_excel_schema';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';

// ##### HELPERS

const normalizeDate = (value) => {
  if (!value) return null;
  let dateObj;
  if (typeof value === 'number') {
    // Excel serial date
    const excelEpoch = new Date(1899, 11, 30);
    dateObj = new Date(excelEpoch.getTime() + value * 86400000);
  } else if (typeof value === 'string') {
    dateObj = new Date(value);
  } else {
    dateObj = value;
  }
  return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : null;
};

const normalizeNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : Number(value);
  return isNaN(num) ? null : parseFloat(num.toFixed(decimals));
};

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    return lower === 'true' || lower === '1' || lower === 'yes' || lower === 's' || lower === 'sim' || lower === 'verdadeiro';
  }
  return value ? true : false;
};

// ##### MAIN COMPONENT

const CardAssetsImport = ({ reload }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const setFileFromFile = (file) => {
    if (file) {
      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const resetState = () => {
    setSelectedFile(null);
    setImportResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileFromFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setFileFromFile(file);
    } else if (file) {
      toast.error('Please use an Excel file (.xlsx or .xls).');
    }
  };

  const processSheet = (workbook, sheetName) => {
    if (!workbook.SheetNames.includes(sheetName)) {
      return null; // Sheet doesn't exist, skip
    }
    const worksheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(worksheet);
    return data.length > 0 ? data : null;
  };

  const normalizeLocations = (data) => {
    return data
      .map((row) => ({
        location_name: String(row.location_name || '').trim(),
        type: row.type || 'other',
        is_active: normalizeBoolean(row.is_active !== undefined ? row.is_active : true),
      }))
      .filter((row) => row.location_name);
  };

  const normalizeCategories = (data) => {
    return data
      .map((row) => ({
        category_code: String(row.category_code || '').trim(),
        order: normalizeNumber(row.order, 0) || 0,
        level_0: String(row.level_0 || '').trim(),
        level_1: String(row.level_1 || '').trim(),
        level_2: String(row.level_2 || '').trim(),
        level_3: String(row.level_3 || '').trim(),
      }))
      .filter((row) => row.category_code && row.level_0);
  };

  const normalizeAssets = (data) => {
    return data
      .map((row) => ({
        asset_code: String(row.asset_code || '').trim(),
        asset_counter: normalizeNumber(row.asset_counter, 0) || null,
        asset_name: String(row.asset_name || '').trim(),
        asset_type: row.asset_type || null,
        category_code: String(row.category_code || '').trim(),
        location_name: row.location_name ? String(row.location_name).trim() : null,
        external_ticker: row.external_ticker ? String(row.external_ticker).trim() : null,
        is_updated: normalizeBoolean(row.is_updated !== undefined ? row.is_updated : false),
        is_active: normalizeBoolean(row.is_active !== undefined ? row.is_active : true),
        update_mode: row.update_mode ? String(row.update_mode).trim() : 'not_applied',
      }))
      .filter((row) => row.asset_code && row.asset_name && row.category_code);
  };

  const normalizeOperations = (data) => {
    return data
      .map((row) => ({
        asset_code: String(row.asset_code || '').trim(),
        operation_status: String(row.operation_status || '').trim(),
        operation_date: normalizeDate(row.operation_date),
        redeem_date: normalizeDate(row.redeem_date),
        quantity: normalizeNumber(row.quantity, 8),
        total_value: normalizeNumber(row.total_value, 2),
        net_value: normalizeNumber(row.net_value, 2),
        rate_description: row.rate_description ? String(row.rate_description).trim() : null,
        notes: row.notes ? String(row.notes).trim() : null,
      }))
      .filter((row) => row.asset_code && row.operation_status && row.operation_date);
  };

  const normalizeReturns = (data) => {
    return data
      .map((row) => ({
        asset_code: String(row.asset_code || '').trim(),
        payment_type: String(row.payment_type || '').trim(),
        payment_date: normalizeDate(row.payment_date),
        payment_value: normalizeNumber(row.payment_value, 2),
        quantity_at_time: normalizeNumber(row.quantity_at_time, 8),
        notes: row.notes ? String(row.notes).trim() : null,
      }))
      .filter((row) => row.asset_code && row.payment_type && row.payment_date);
  };

  const normalizeSnapshots = (data) => {
    return data
      .map((row) => ({
        snapshot_date: normalizeDate(row.snapshot_date),
        is_closing: normalizeBoolean(row.is_closing !== undefined ? row.is_closing : true),
        notes: row.notes ? String(row.notes).trim() : null,
      }))
      .filter((row) => row.snapshot_date);
  };

  const normalizeMarketSource = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      if (trimmed === 'none' || trimmed === '') return null;
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return typeof value === 'object' && value !== null ? value : null;
  };

  const normalizeSnapshotItems = (data) => {
    return data
      .map((row) => ({
        snapshot_date: normalizeDate(row.snapshot_date),
        asset_code: String(row.asset_code || '').trim(),
        snapshot_source: (row.snapshot_source != null && String(row.snapshot_source).trim() !== '')
          ? String(row.snapshot_source).trim()
          : 'manually',
        snapshot_value: normalizeNumber(row.snapshot_value, 2),
        snapshot_quantity: normalizeNumber(row.snapshot_quantity, 8) || 1,
        market_unitary_value: normalizeNumber(row.market_unitary_value, 8),
        market_source: normalizeMarketSource(row.market_source),
      }))
      .filter((row) => row.snapshot_date && row.asset_code && row.snapshot_value !== null);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setIsImporting(true);
    setImportResults(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });

        // Parse all sheets and normalize data
        const locationsData = processSheet(workbook, SHEET_NAMES.LOCATIONS);
        const categoriesData = processSheet(workbook, SHEET_NAMES.CATEGORIES);
        const assetsData = processSheet(workbook, SHEET_NAMES.ASSETS);
        const operationsData = processSheet(workbook, SHEET_NAMES.OPERATIONS);
        const returnsData = processSheet(workbook, SHEET_NAMES.RETURNS);
        const snapshotsData = processSheet(workbook, SHEET_NAMES.SNAPSHOTS);
        const snapshotItemsData = processSheet(workbook, SHEET_NAMES.SNAPSHOT_ITEMS);

        // Build payload for single API call
        const payload = {};
        
        if (locationsData) {
          const normalized = normalizeLocations(locationsData);
          if (normalized.length > 0) payload.locations = normalized;
        }
        
        if (categoriesData) {
          const normalized = normalizeCategories(categoriesData);
          if (normalized.length > 0) payload.categories = normalized;
        }
        
        if (assetsData) {
          const normalized = normalizeAssets(assetsData);
          if (normalized.length > 0) payload.assets = normalized;
        }
        
        if (operationsData) {
          const normalized = normalizeOperations(operationsData);
          if (normalized.length > 0) payload.operations = normalized;
        }
        
        if (returnsData) {
          const normalized = normalizeReturns(returnsData);
          if (normalized.length > 0) payload.returns = normalized;
        }
        
        if (snapshotsData) {
          const normalized = normalizeSnapshots(snapshotsData);
          if (normalized.length > 0) payload.snapshots = normalized;
        }
        
        if (snapshotItemsData) {
          const normalized = normalizeSnapshotItems(snapshotItemsData);
          if (normalized.length > 0) payload.snapshot_items = normalized;
        }

        // Check if payload has any data
        const hasData = Object.keys(payload).length > 0;
        if (!hasData) {
          toast.error('No data found in the Excel file. Check that sheets contain rows.');
          setIsImporting(false);
          return;
        }

        // Single API call
        const res = await importAssetsExcel(payload);
        const apiResults = res.data.results || {};
        
        // Map API results to component state format
        const results = {
          locations: {
            created: apiResults.locations?.created || 0,
            errors: apiResults.locations?.errors || []
          },
          categories: {
            created: apiResults.categories?.created || 0,
            errors: apiResults.categories?.errors || []
          },
          assets: {
            created: apiResults.assets?.created || 0,
            errors: apiResults.assets?.errors || []
          },
          operations: {
            created: apiResults.operations?.created || 0,
            errors: apiResults.operations?.errors || []
          },
          returns: {
            created: apiResults.returns?.created || 0,
            errors: apiResults.returns?.errors || []
          },
          snapshots: {
            created: apiResults.snapshots?.created || 0,
            errors: apiResults.snapshots?.errors || []
          },
          snapshotItems: {
            created: apiResults.snapshot_items?.created || 0,
            errors: apiResults.snapshot_items?.errors || []
          },
        };

        setImportResults(results);

        // Show summary toast
        const summary = res.data.summary || {};
        const totalCreated = summary.total_created || 0;
        const totalErrors = summary.total_errors || 0;

        if (totalErrors > 0) {
          toast.error(`Imported ${totalCreated} record(s). ${totalErrors} error(s) found.`);
        } else {
          toast.success(`Imported ${totalCreated} record(s) successfully.`);
        }

        resetState();
        if (reload) reload();
      } catch (err) {
        console.error('Import error:', err);
        console.error('Error details:', err.response?.data || err.message);
        toast.error(
          `Failed to process Excel file: ${err.response?.data?.error || err.message || 'Unknown error'}`
        );
      } finally {
        setIsImporting(false);
      }
    };

    reader.readAsBinaryString(selectedFile);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await exportAssetsExcel();
      const payload = res.data;
      const wb = buildWorkbookFromPayload(payload, XLSX);
      XLSX.writeFile(wb, 'assets_export.xlsx');
      toast.success('Export completed.');
      if (reload) reload();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Export failed.');
    } finally {
      setIsExporting(false);
    }
  };

  // No-op for legacy/cache: "Update snapshot positions" moved to CardAssetsUpdateData
  const handleUpdateSnapshotPositions = () => {};

  const handleConfirmDeleteAll = async () => {
    setIsDeleting(true);
    try {
      await deleteAllAssetsData();
      setShowDeleteAllModal(false);
      toast.success('All asset module data was removed.');
      if (reload) reload();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Delete failed.');
    } finally {
      setIsDeleting(false);
    }
  };

  const buttonBase = 'px-6 py-3 rounded-xl shadow-md text-white';
  const importDisabled = 'bg-gray-400 cursor-not-allowed';
  const importEnabled = 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <>
      <div className="h-auto bg-white shadow rounded-xl p-4 flex flex-col gap-4">
        {/* Header */}
        <div className="bg-green-800 border-b border-gray-200 -m-4 mb-4 rounded-t-xl">
          <div className="px-3 py-2 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CloudArrowUpIcon className="h-4 w-4 text-white" />
              <h2 className="text-sm font-medium text-white">Import / Export Excel</h2>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-stretch">
          {/* Left: drag/drop + select zone */}
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex-1 min-h-[120px] flex items-center justify-center rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
              isDragging
                ? 'border-green-600 bg-green-50'
                : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-green-500'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <span className="text-sm font-medium text-gray-600">
              {isDragging ? 'Drop file here' : 'Select or drag file'}
            </span>
          </div>

          {/* Center: uploaded file status + Import button */}
          <div className="flex flex-col justify-center gap-2 sm:min-w-[160px]">
            <div className="text-sm text-gray-700">
              Uploaded file: <strong>{selectedFile?.name || 'none'}</strong>
            </div>
            <button
              onClick={handleImport}
              disabled={!selectedFile || isImporting}
              className={`${buttonBase} ${!selectedFile || isImporting ? importDisabled : importEnabled}`}
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>
          </div>

          {/* Right: Export + Delete stacked */}
          <div className="flex flex-col gap-2 sm:min-w-[140px]">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`${buttonBase} ${isExporting ? importDisabled : 'bg-blue-600 hover:bg-blue-700'}`}
              title="Export data to Excel (same structure as import)"
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
            <button
              onClick={() => setShowDeleteAllModal(true)}
              disabled={isDeleting}
              className={`${buttonBase} ${isDeleting ? importDisabled : 'bg-red-600 hover:bg-red-700'}`}
              title="Delete all Locations, Categories, Assets, Operations, Returns, Snapshots and Balances"
            >
              Delete all data
            </button>
          </div>
        </div>

        {/* Results Summary */}
        {importResults && (
          <div className="mt-2 space-y-2">
            <div className="text-sm font-semibold text-gray-800">Import Summary:</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {importResults.locations.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Locations: {importResults.locations.created}</div>
                </div>
              )}
              {importResults.categories.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Categories: {importResults.categories.created}</div>
                </div>
              )}
              {importResults.assets.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Assets: {importResults.assets.created}</div>
                </div>
              )}
              {importResults.operations.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Operations: {importResults.operations.created}</div>
                </div>
              )}
              {importResults.returns.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Returns: {importResults.returns.created}</div>
                </div>
              )}
              {importResults.snapshots.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Snapshots: {importResults.snapshots.created}</div>
                </div>
              )}
              {importResults.snapshotItems.created > 0 && (
                <div className="bg-green-50 p-2 rounded">
                  <div className="font-medium">Snapshot Items: {importResults.snapshotItems.created}</div>
                </div>
              )}
            </div>

            {/* Errors */}
            {[
              ...importResults.locations.errors,
              ...importResults.categories.errors,
              ...importResults.assets.errors,
              ...importResults.operations.errors,
              ...importResults.returns.errors,
              ...importResults.snapshots.errors,
              ...importResults.snapshotItems.errors,
            ].length > 0 && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-sm font-semibold text-red-800 mb-2">Errors:</div>
                <div className="space-y-1 text-xs text-red-700 max-h-40 overflow-y-auto">
                  {[
                    ...importResults.locations.errors.map((e) => ({ ...e, sheet: 'Locations' })),
                    ...importResults.categories.errors.map((e) => ({ ...e, sheet: 'Categories' })),
                    ...importResults.assets.errors.map((e) => ({ ...e, sheet: 'Assets' })),
                    ...importResults.operations.errors.map((e) => ({ ...e, sheet: 'Operations' })),
                    ...importResults.returns.errors.map((e) => ({ ...e, sheet: 'Returns' })),
                    ...importResults.snapshots.errors.map((e) => ({ ...e, sheet: 'Snapshots' })),
                    ...importResults.snapshotItems.errors.map((e) => ({ ...e, sheet: 'Balances' })),
                  ].map((error, idx) => (
                    <div key={idx}>
                      Row {error.row || error.index + 2} ({error.sheet}): {error.message || error.value}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalConfirm
        isOpen={showDeleteAllModal}
        onClose={() => !isDeleting && setShowDeleteAllModal(false)}
        onConfirm={handleConfirmDeleteAll}
        title="Delete all asset data"
        message="This will delete all Locations, Categories, Assets, Operations, Returns, Snapshots, and Balances. This cannot be undone. Are you sure?"
        confirmText={isDeleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        confirmButtonClass={`px-4 py-2 rounded-xl ${isDeleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white shadow`}
        cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${isDeleting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        disabled={isDeleting}
      />
    </>
  );
};

export default CardAssetsImport;
